import React, { useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { RadioGroup, FormSpacer, Form } from 'components';
import { FormStepContentProps } from 'types';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { useScrollTop } from 'hooks/useScrollTop';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import validationSchema from './schema';
import { setTransactionStartEvent, setFormStartEvent, setFormCompleteEvent } from 'hooks/useAnalytics';
import { yupResolver } from '@hookform/resolvers/yup';

const LandingForm = ({ defaultValues, onComplete }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { getTextContent } = useContent({ context: useContentContext() });

  const { formData, updateForm } = useFormState();

  const form = useForm<FormDataType>({
    mode: 'onChange',
    defaultValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema) as any,
  });

  const { handleSubmit, watch, control } = form;
  const formRef = useRef(null);
  const isValid = validationSchema.isValidSync(watch());

  // Localized content constants
  const pageTitle = getTextContent('landing.form.heading');
  const pageDescription = getTextContent('landing.form.contentParagraph');
  const sectionHeading = getTextContent('landing.form.sectionHeading');
  const lodgement0Label = getTextContent('landing.form.lodgement.0.label');
  const lodgement1Label = getTextContent('landing.form.lodgement.1.label');
  const lodgement2Label = getTextContent('landing.form.lodgement.2.label');
  const lodgement3Label = getTextContent('landing.form.lodgement.3.label');
  const submitLabel = getTextContent('landing.form.submit.label');

  const onSubmit = (newData: Partial<FormDataType>) => {
    setFormCompleteEvent();
    onComplete(newData);

    if (formData?.lodgement !== newData.lodgement) {
      updateForm({
        files: [],
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} ref={formRef} data-test="landing-form">
      <FormCard variant="pageTitle" title={pageTitle} description={pageDescription} />
      <FormCard variant="content">
        <Controller
          name="lodgement"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <RadioGroup
              {...field}
              title={sectionHeading}
              styleVariant="skinny"
              value={field.value}
              data-test="lodgement"
              data-track="lodgement"
              data-track-value="capture"
              radioList={[
                {
                  id: 'Complaint',
                  label: lodgement0Label,
                  value: 'Complaint',
                },
                {
                  id: 'Feedback',
                  label: lodgement1Label,
                  value: 'Feedback',
                },
                {
                  id: 'Compliment',
                  label: lodgement2Label,
                  value: 'Compliment',
                },
                {
                  id: 'Financial-hardship-application',
                  label: lodgement3Label,
                  value: 'Financial hardship application',
                },
              ]}
              onChange={(value) => {
                field.onChange(value);
                setTransactionStartEvent();
                setFormStartEvent();
              }}
            />
          )}
        />

        <FormSpacer type="formgroup" />
        <Button label={submitLabel} type="submit" disabled={!isValid} />
      </FormCard>
    </Form>
  );
};

export default LandingForm;
